import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  Search, Users, FileText, Clock, Shield,
  Eye, Download, Trash2, AlertTriangle, CheckCircle,
  XCircle, Loader2, ChevronLeft, ChevronRight, RefreshCw
} from 'lucide-react';

const GDPRDataCentre = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('subjects');
  const [loading, setLoading] = useState(true);
  
  // Data Subjects state
  const [subjects, setSubjects] = useState([]);
  const [subjectsTotal, setSubjectsTotal] = useState(0);
  const [subjectSearch, setSubjectSearch] = useState('');
  const [subjectRole, setSubjectRole] = useState('all');
  const [subjectStatus, setSubjectStatus] = useState('all');
  const [subjectPage, setSubjectPage] = useState(0);
  
  // Open Requests state
  const [requests, setRequests] = useState([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestType, setRequestType] = useState('all');
  const [requestStatus, setRequestStatus] = useState('pending');
  
  // Audit Log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditActionType, setAuditActionType] = useState('all');
  const [auditSearch, setAuditSearch] = useState('');
  const [auditPage, setAuditPage] = useState(0);
  
  // Modal state
  const [viewDataModal, setViewDataModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjectData, setSubjectData] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const LIMIT = 20;

  // Fetch Data Subjects
  const fetchSubjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: subjectPage * LIMIT,
        limit: LIMIT
      };
      if (subjectSearch) params.search = subjectSearch;
      if (subjectRole !== 'all') params.role = subjectRole;
      if (subjectStatus !== 'all') params.status = subjectStatus;
      
      const res = await adminAPI.getGDPRSubjects(params);
      setSubjects(res.data.subjects || []);
      setSubjectsTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch data subjects');
    } finally {
      setLoading(false);
    }
  }, [subjectPage, subjectSearch, subjectRole, subjectStatus]);

  // Fetch Open Requests
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: 0, limit: 100 };
      if (requestType !== 'all') params.request_type = requestType;
      if (requestStatus !== 'all') params.status = requestStatus;
      
      const res = await adminAPI.getGDPRRequests(params);
      setRequests(res.data.requests || []);
      setRequestsTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch GDPR requests');
    } finally {
      setLoading(false);
    }
  }, [requestType, requestStatus]);

  // Fetch Audit Log
  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: auditPage * LIMIT,
        limit: LIMIT
      };
      if (auditActionType !== 'all') params.action_type = auditActionType;
      if (auditSearch) params.search = auditSearch;
      
      const res = await adminAPI.getGDPRAuditLog(params);
      setAuditLogs(res.data.logs || []);
      setAuditTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch audit log');
    } finally {
      setLoading(false);
    }
  }, [auditPage, auditActionType, auditSearch]);

  useEffect(() => {
    if (activeTab === 'subjects') fetchSubjects();
    else if (activeTab === 'requests') fetchRequests();
    else if (activeTab === 'audit') fetchAuditLog();
    else setLoading(false);
  }, [activeTab, fetchSubjects, fetchRequests, fetchAuditLog]);

  // View subject data
  const handleViewData = async (subject) => {
    setSelectedSubject(subject);
    setActionLoading(true);
    try {
      const res = await adminAPI.getGDPRSubjectData(subject.id);
      setSubjectData(res.data);
      setViewDataModal(true);
    } catch (error) {
      toast.error('Failed to fetch subject data');
    } finally {
      setActionLoading(false);
    }
  };

  // Export subject data
  const handleExport = async (subject) => {
    setActionLoading(true);
    try {
      await adminAPI.exportGDPRSubjectData(subject.id);
      toast.success(`Data export sent to ${subject.email}`);
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export data');
    } finally {
      setActionLoading(false);
    }
  };

  // Schedule deletion
  const handleScheduleDeletion = async () => {
    if (!selectedSubject) return;
    setActionLoading(true);
    try {
      const res = await adminAPI.scheduleGDPRDeletion(selectedSubject.id, { reason: deleteReason });
      toast.success(`Deletion scheduled for ${res.data.scheduled_for}`);
      setDeleteModal(false);
      setDeleteReason('');
      fetchSubjects();
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule deletion');
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel deletion
  const handleCancelDeletion = async (subject) => {
    setActionLoading(true);
    try {
      await adminAPI.cancelGDPRDeletion(subject.id);
      toast.success('Deletion cancelled');
      fetchSubjects();
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel deletion');
    } finally {
      setActionLoading(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400',
      deletion_requested: 'bg-amber-500/20 text-amber-400',
      deleted: 'bg-red-500/20 text-red-400',
      pending: 'bg-amber-500/20 text-amber-400',
      completed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-gray-500/20 text-gray-400'
    };
    const labels = {
      active: 'Active',
      deletion_requested: 'Deletion Requested',
      deleted: 'Deleted',
      pending: 'Pending',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Action type badge for audit log
  const ActionBadge = ({ action }) => {
    const styles = {
      View: 'bg-blue-500/20 text-blue-400',
      Export: 'bg-purple-500/20 text-purple-400',
      Deletion: 'bg-red-500/20 text-red-400',
      Correction: 'bg-amber-500/20 text-amber-400'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[action] || 'bg-gray-500/20 text-gray-400'}`}>
        {action}
      </span>
    );
  };

  // Request type badge
  const RequestTypeBadge = ({ type }) => {
    const styles = {
      access: 'bg-blue-500/20 text-blue-400',
      rectification: 'bg-amber-500/20 text-amber-400',
      erasure: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      access: 'Art. 15 Access',
      rectification: 'Art. 16 Rectification',
      erasure: 'Art. 17 Erasure'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[type] || 'bg-gray-500/20 text-gray-400'}`}>
        {labels[type] || type}
      </span>
    );
  };

  const tabs = [
    { id: 'subjects', label: 'Data Subjects', icon: Users },
    { id: 'requests', label: 'Open Requests', icon: FileText },
    { id: 'audit', label: 'Audit Log', icon: Clock },
    { id: 'retention', label: 'Retention Policy', icon: Shield }
  ];

  return (
    <AdminLayout title="GDPR Data Centre" breadcrumbs={[{ label: 'GDPR' }]}>
      <div className="space-y-6" data-testid="gdpr-data-centre">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E8EDF5] pb-4">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab.id)}
              className={`gap-2 ${activeTab === tab.id ? 'bg-[#1B5AFF] hover:bg-[#1B5AFF]/90' : ''}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'subjects' && (
          <div className="space-y-4">
            {/* Filters */}
            <Card className="bg-[#FFFFFF] border-[#E8EDF5] p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-[#5B6472] text-xs mb-1">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
                    <Input
                      placeholder="Search by email or VETT ID..."
                      value={subjectSearch}
                      onChange={(e) => setSubjectSearch(e.target.value)}
                      className="pl-10 bg-[#FFFFFF] border-[#E8EDF5]"
                      data-testid="subject-search"
                    />
                  </div>
                </div>
                <div className="w-40">
                  <Label className="text-[#5B6472] text-xs mb-1">Role</Label>
                  <Select value={subjectRole} onValueChange={setSubjectRole}>
                    <SelectTrigger className="bg-[#FFFFFF] border-[#E8EDF5]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="employer">Employer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-48">
                  <Label className="text-[#5B6472] text-xs mb-1">Status</Label>
                  <Select value={subjectStatus} onValueChange={setSubjectStatus}>
                    <SelectTrigger className="bg-[#FFFFFF] border-[#E8EDF5]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="deletion_requested">Deletion Requested</SelectItem>
                      <SelectItem value="deleted">Deleted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchSubjects} variant="outline" className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </Card>

            {/* Subjects Table */}
            <Card className="bg-[#FFFFFF] border-[#E8EDF5] overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#1B5AFF]" />
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#FFFFFF]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">VETT ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Role</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Joined</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-[#5B6472] uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E8EDF5]">
                        {subjects.map((subject) => (
                          <tr key={subject.id} className="hover:bg-[#FFFFFF]/50" data-testid="subject-row">
                            <td className="px-4 py-3 text-sm text-[#0A0F1E]">{subject.name}</td>
                            <td className="px-4 py-3 text-sm text-[#5B6472]">{subject.email}</td>
                            <td className="px-4 py-3 text-sm text-[#5B6472] font-mono">{subject.vett_id || '-'}</td>
                            <td className="px-4 py-3 text-sm text-[#5B6472] capitalize">{subject.role}</td>
                            <td className="px-4 py-3"><StatusBadge status={subject.status} /></td>
                            <td className="px-4 py-3 text-sm text-[#5B6472]">{formatDate(subject.joined_at)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleViewData(subject)}
                                  className="h-8 px-2"
                                  disabled={actionLoading || subject.status === 'deleted'}
                                  data-testid="view-data-btn"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleExport(subject)}
                                  className="h-8 px-2"
                                  disabled={actionLoading || subject.status === 'deleted'}
                                  data-testid="export-btn"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                {subject.status === 'deletion_requested' ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleCancelDeletion(subject)}
                                    className="h-8 px-2 text-amber-400 hover:text-amber-300"
                                    disabled={actionLoading}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                ) : subject.status !== 'deleted' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => { setSelectedSubject(subject); setDeleteModal(true); }}
                                    className="h-8 px-2 text-red-400 hover:text-red-300"
                                    disabled={actionLoading}
                                    data-testid="delete-btn"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {subjects.length === 0 && (
                          <tr>
                            <td colSpan={7} className="px-4 py-12 text-center text-[#5B6472]">
                              No data subjects found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8EDF5]">
                    <p className="text-sm text-[#5B6472]">
                      Showing {subjectPage * LIMIT + 1}-{Math.min((subjectPage + 1) * LIMIT, subjectsTotal)} of {subjectsTotal}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSubjectPage(p => Math.max(0, p - 1))}
                        disabled={subjectPage === 0}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSubjectPage(p => p + 1)}
                        disabled={(subjectPage + 1) * LIMIT >= subjectsTotal}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="space-y-4">
            {/* Filters */}
            <Card className="bg-[#FFFFFF] border-[#E8EDF5] p-4">
              <div className="flex gap-4 items-end">
                <div className="w-48">
                  <Label className="text-[#5B6472] text-xs mb-1">Request Type</Label>
                  <Select value={requestType} onValueChange={setRequestType}>
                    <SelectTrigger className="bg-[#FFFFFF] border-[#E8EDF5]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="access">Art. 15 Access</SelectItem>
                      <SelectItem value="rectification">Art. 16 Rectification</SelectItem>
                      <SelectItem value="erasure">Art. 17 Erasure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <Label className="text-[#5B6472] text-xs mb-1">Status</Label>
                  <Select value={requestStatus} onValueChange={setRequestStatus}>
                    <SelectTrigger className="bg-[#FFFFFF] border-[#E8EDF5]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchRequests} variant="outline" className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </Card>

            {/* Requests Table */}
            <Card className="bg-[#FFFFFF] border-[#E8EDF5] overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#1B5AFF]" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FFFFFF]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Subject</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Received</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Deadline</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Days Left</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8EDF5]">
                      {requests.map((req) => (
                        <tr key={req.id} className="hover:bg-[#FFFFFF]/50" data-testid="request-row">
                          <td className="px-4 py-3"><RequestTypeBadge type={req.type} /></td>
                          <td className="px-4 py-3 text-sm text-[#0A0F1E]">{req.subject_email}</td>
                          <td className="px-4 py-3 text-sm text-[#5B6472]">{formatDate(req.requested_at)}</td>
                          <td className="px-4 py-3 text-sm text-[#5B6472]">{formatDate(req.deadline)}</td>
                          <td className="px-4 py-3">
                            {req.days_remaining !== null ? (
                              <span className={`flex items-center gap-1 text-sm ${
                                req.sla_warning ? 'text-red-400' : 'text-[#5B6472]'
                              }`}>
                                {req.sla_warning && <AlertTriangle className="w-4 h-4" />}
                                {req.days_remaining} days
                              </span>
                            ) : (
                              <span className="text-[#5B6472] text-sm">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={req.status} /></td>
                        </tr>
                      ))}
                      {requests.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-12 text-center text-[#5B6472]">
                            No GDPR requests found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="space-y-4">
            {/* Filters */}
            <Card className="bg-[#FFFFFF] border-[#E8EDF5] p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-[#5B6472] text-xs mb-1">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
                    <Input
                      placeholder="Search by admin or subject email..."
                      value={auditSearch}
                      onChange={(e) => setAuditSearch(e.target.value)}
                      className="pl-10 bg-[#FFFFFF] border-[#E8EDF5]"
                      data-testid="audit-search"
                    />
                  </div>
                </div>
                <div className="w-48">
                  <Label className="text-[#5B6472] text-xs mb-1">Action Type</Label>
                  <Select value={auditActionType} onValueChange={setAuditActionType}>
                    <SelectTrigger className="bg-[#FFFFFF] border-[#E8EDF5]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      <SelectItem value="View">View</SelectItem>
                      <SelectItem value="Export">Export</SelectItem>
                      <SelectItem value="Deletion">Deletion</SelectItem>
                      <SelectItem value="Correction">Correction</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchAuditLog} variant="outline" className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </Card>

            {/* Audit Log Table */}
            <Card className="bg-[#FFFFFF] border-[#E8EDF5] overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#1B5AFF]" />
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#FFFFFF]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Timestamp</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Admin Actor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Action Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Subject</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E8EDF5]">
                        {auditLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-[#FFFFFF]/50" data-testid="audit-row">
                            <td className="px-4 py-3 text-sm text-[#5B6472]">{formatDateTime(log.timestamp)}</td>
                            <td className="px-4 py-3 text-sm text-[#0A0F1E]">{log.admin_email}</td>
                            <td className="px-4 py-3"><ActionBadge action={log.action_type} /></td>
                            <td className="px-4 py-3 text-sm text-[#5B6472]">{log.subject_email}</td>
                            <td className="px-4 py-3 text-sm text-[#5B6472]">
                              {log.details?.action || '-'}
                            </td>
                          </tr>
                        ))}
                        {auditLogs.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-4 py-12 text-center text-[#5B6472]">
                              No audit logs found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8EDF5]">
                    <p className="text-sm text-[#5B6472]">
                      Showing {auditPage * LIMIT + 1}-{Math.min((auditPage + 1) * LIMIT, auditTotal)} of {auditTotal}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAuditPage(p => Math.max(0, p - 1))}
                        disabled={auditPage === 0}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAuditPage(p => p + 1)}
                        disabled={(auditPage + 1) * LIMIT >= auditTotal}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'retention' && (
          <div className="space-y-4">
            <Card className="bg-[#FFFFFF] border-[#E8EDF5] p-6">
              <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#1B5AFF]" />
                Data Retention Policy
              </h2>
              <p className="text-[#5B6472] text-sm mb-6">
                The following retention rules apply to data categories within the VETT platform, 
                in compliance with GDPR and applicable regulations.
              </p>
              
              <div className="space-y-4">
                <div className="bg-[#FFFFFF] rounded-lg p-4 border border-[#E8EDF5]">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-[#0A0F1E] font-medium">Account Data</h3>
                      <p className="text-[#5B6472] text-sm mt-1">
                        User credentials, email, profile information, preferences
                      </p>
                    </div>
                    <span className="text-[#1B5AFF] font-mono text-sm">Until deletion request + 72h</span>
                  </div>
                </div>

                <div className="bg-[#FFFFFF] rounded-lg p-4 border border-[#E8EDF5]">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-[#0A0F1E] font-medium">Verified Records (VETT Records)</h3>
                      <p className="text-[#5B6472] text-sm mt-1">
                        Employment records, ratings, assessments. Anonymized on deletion, not deleted.
                      </p>
                    </div>
                    <span className="text-amber-400 font-mono text-sm">Permanent (anonymized)</span>
                  </div>
                </div>

                <div className="bg-[#FFFFFF] rounded-lg p-4 border border-[#E8EDF5]">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-[#0A0F1E] font-medium">Stripe / Billing Data</h3>
                      <p className="text-[#5B6472] text-sm mt-1">
                        Payment records, invoices, subscription history (legal requirement)
                      </p>
                    </div>
                    <span className="text-blue-400 font-mono text-sm">7 years</span>
                  </div>
                </div>

                <div className="bg-[#FFFFFF] rounded-lg p-4 border border-[#E8EDF5]">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-[#0A0F1E] font-medium">Messages</h3>
                      <p className="text-[#5B6472] text-sm mt-1">
                        Direct messages between users
                      </p>
                    </div>
                    <span className="text-green-400 font-mono text-sm">180 days</span>
                  </div>
                </div>

                <div className="bg-[#FFFFFF] rounded-lg p-4 border border-[#E8EDF5]">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-[#0A0F1E] font-medium">Audit Logs (GDPR)</h3>
                      <p className="text-[#5B6472] text-sm mt-1">
                        Immutable record of all GDPR-related admin actions
                      </p>
                    </div>
                    <span className="text-purple-400 font-mono text-sm">3 years minimum</span>
                  </div>
                </div>

                <div className="bg-[#FFFFFF] rounded-lg p-4 border border-[#E8EDF5]">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-[#0A0F1E] font-medium">Deletion Hold Period</h3>
                      <p className="text-[#5B6472] text-sm mt-1">
                        Time between deletion request and execution (allows cancellation)
                      </p>
                    </div>
                    <span className="text-red-400 font-mono text-sm">72 hours</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-amber-400 font-medium">Important Notes</h4>
                    <ul className="text-[#5B6472] text-sm mt-2 space-y-1 list-disc list-inside">
                      <li>Verified records are anonymized, not deleted, to maintain employer record integrity</li>
                      <li>Users can request data export at any time (Art. 15 access request)</li>
                      <li>Deletion requests have a 30-day legal deadline for completion</li>
                      <li>All admin GDPR actions are logged to an immutable audit trail</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* View Data Modal */}
        <Dialog open={viewDataModal} onOpenChange={setViewDataModal}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#0A0F1E]">Subject Data - {selectedSubject?.email}</DialogTitle>
              <DialogDescription className="text-[#5B6472]">
                Complete data held for this user (Art. 15 access)
              </DialogDescription>
            </DialogHeader>
            {subjectData && (
              <div className="space-y-4 mt-4">
                <div>
                  <h4 className="text-sm font-medium text-[#5B6472] mb-2">Account</h4>
                  <pre className="bg-[#FFFFFF] p-3 rounded text-xs text-[#0A0F1E] overflow-x-auto">
                    {JSON.stringify(subjectData.user, null, 2)}
                  </pre>
                </div>
                {subjectData.profile && (
                  <div>
                    <h4 className="text-sm font-medium text-[#5B6472] mb-2">Profile</h4>
                    <pre className="bg-[#FFFFFF] p-3 rounded text-xs text-[#0A0F1E] overflow-x-auto">
                      {JSON.stringify(subjectData.profile, null, 2)}
                    </pre>
                  </div>
                )}
                {subjectData.records?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-[#5B6472] mb-2">Records ({subjectData.records.length})</h4>
                    <pre className="bg-[#FFFFFF] p-3 rounded text-xs text-[#0A0F1E] overflow-x-auto max-h-40">
                      {JSON.stringify(subjectData.records, null, 2)}
                    </pre>
                  </div>
                )}
                {subjectData.messages?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-[#5B6472] mb-2">Messages ({subjectData.messages.length})</h4>
                    <pre className="bg-[#FFFFFF] p-3 rounded text-xs text-[#0A0F1E] overflow-x-auto max-h-40">
                      {JSON.stringify(subjectData.messages, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDataModal(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5]">
            <DialogHeader>
              <DialogTitle className="text-[#0A0F1E] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Schedule Account Deletion
              </DialogTitle>
              <DialogDescription className="text-[#5B6472]">
                This will schedule deletion of all data for {selectedSubject?.email}.
                A 72-hour hold period will apply before execution.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 my-4">
              <div>
                <Label className="text-[#5B6472] text-sm">Reason (optional)</Label>
                <Textarea
                  placeholder="GDPR Art. 17 erasure request..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="mt-1 bg-[#FFFFFF] border-[#E8EDF5]"
                />
              </div>
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-sm text-[#5B6472]">
                <p className="font-medium text-amber-400 mb-1">What will happen:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>User will be notified via email</li>
                  <li>72-hour hold period starts</li>
                  <li>After hold: PII deleted, records anonymized</li>
                  <li>Stripe subscription cancelled</li>
                </ul>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteModal(false)} disabled={actionLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleScheduleDeletion}
                disabled={actionLoading}
                className="bg-red-500 hover:bg-red-600"
                data-testid="confirm-delete-btn"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Schedule Deletion
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default GDPRDataCentre;
